import { Tooltip } from 'antd';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_ICON_SIZE } from '../../constants/AIAgentConstants';
import { useOscarTheme } from '../../context/OscarRoleContext';

interface OscarIconButtonProps {
	icon: React.ReactNode;
	/** Accessible name; also used as the hover tooltip. */
	label: string;
	onClick?: () => void;
	/** Rendered glyph size in px. Figma: 20 for the action row, 16 for close. */
	size?: number;
	disabled?: boolean;
	className?: string;
}

/**
 * Bare icon affordance used in the Oscar panel header. The design has no
 * button chrome — just the glyph — so this keeps the hit area at the glyph box
 * and expresses state through color alone.
 *
 * The Tooltip wraps a span, never the button itself. antd 4's Tooltip, handed
 * a *disabled* `<button>`, moves its className onto a wrapper of its own and
 * clones the button with `className: null` — so `bg-transparent` and
 * `border-none` went with it and the browser's default button box showed
 * behind the glyph (OPE-5443: the square behind New chat while fullscreen is
 * still connecting). The span also keeps the tooltip working while disabled,
 * since a disabled button swallows the hover; `pointer-events-none` lets it
 * through to the span.
 */
export const OscarIconButton = ({
	icon,
	label,
	onClick,
	size = OSCAR_ICON_SIZE.action,
	disabled,
	className,
}: OscarIconButtonProps) => {
	const theme = useOscarTheme();
	return (
		<Tooltip title={label}>
			<span className={cn('flex shrink-0', disabled && 'cursor-not-allowed')}>
				<button
					type="button"
					aria-label={label}
					disabled={disabled}
					onClick={onClick}
					style={{ width: size, height: size, fontSize: size }}
					className={cn(
						'flex cursor-pointer items-center justify-center border-none bg-transparent p-0 leading-none text-character-primary transition-colors',
						theme.accentHoverText,
						disabled && 'pointer-events-none text-character-disabled',
						className
					)}
				>
					{icon}
				</button>
			</span>
		</Tooltip>
	);
};

export default OscarIconButton;
